import { Accordion } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';

import { TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';
import { dateCol, dateTimeCol, moneyCol, numberCol, textCol } from '@/cip/widgets/Table/columnHelpers/colDef';

import { DetailsBlock } from './DetailsBlock';
import { BondLadderTradePlanBuyDetails, BondLadderTradePlanSellDetails, TradeDetailRecord } from './tradeDetailsModel';

export const BondLadderDetails = ({
    tradePlanDetails,
}: {
    tradePlanDetails: Partial<Record<TradePlanDetailType, TradeDetailRecord>>;
}) => {
    const sellDetails = tradePlanDetails[TradePlanDetailType.SELL_DEBUG]?.detailJson;
    const buyDetails = tradePlanDetails[TradePlanDetailType.BUY_DEBUG]?.detailJson;

    return (
        <Accordion allowMultiple>
            {sellDetails && (
                <DetailsBlock
                    data={sellDetails as BondLadderTradePlanSellDetails[]}
                    columns={sellDetailsColumns}
                    columnSettingsKey="bondLadderDetailsSellDetails"
                    title="Sell Details"
                    initialSort={{ id: 'quantity', desc: true }}
                />
            )}
            {buyDetails && (
                <DetailsBlock
                    data={buyDetails as BondLadderTradePlanBuyDetails[]}
                    columns={buyDetailsColumns}
                    columnSettingsKey="bondLadderDetailsBuyDetails"
                    title="Buy Details"
                />
            )}
        </Accordion>
    );
};

const sellDetailsColumnHelper = createColumnHelper<BondLadderTradePlanSellDetails>();
const sellDetailsColumns = [
    sellDetailsColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    sellDetailsColumnHelper.accessor('symbol', textCol({ id: 'symbol' })),
    sellDetailsColumnHelper.accessor('positionQuantity', numberCol({ id: 'positionQuantity' })),
    sellDetailsColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    sellDetailsColumnHelper.accessor('currentExposure', moneyCol({ id: 'currentExposure' })),
    sellDetailsColumnHelper.accessor('dirtyPrice', moneyCol({ id: 'dirtyPrice' })),
    sellDetailsColumnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    sellDetailsColumnHelper.accessor('finalBudget', moneyCol({ id: 'finalBudget' })),
    sellDetailsColumnHelper.accessor('grossBudget', moneyCol({ id: 'grossBudget' })),
    sellDetailsColumnHelper.accessor('exposureCumsum', moneyCol({ id: 'exposureCumsum' })),
    sellDetailsColumnHelper.accessor('budgetMinusExposureCumsum', moneyCol({ id: 'budgetMinusExposureCumsum' })),
    sellDetailsColumnHelper.accessor('netTradeBudget', moneyCol({ id: 'netTradeBudget' })),
    sellDetailsColumnHelper.accessor('realTradeBudget', moneyCol({ id: 'realTradeBudget' })),
    sellDetailsColumnHelper.accessor('quantityCalculated', numberCol({ id: 'quantityCalculated' })),
    sellDetailsColumnHelper.accessor('contractMultiplier', numberCol({ id: 'contractMultiplier' })),
    sellDetailsColumnHelper.accessor('priceMultiplier', numberCol({ id: 'priceMultiplier' })),
    sellDetailsColumnHelper.accessor('maturityDate', dateCol({ id: 'maturityDate' })),
    sellDetailsColumnHelper.accessor('askPrice', moneyCol({ id: 'askPrice' })),
    sellDetailsColumnHelper.accessor('bidPrice', moneyCol({ id: 'bidPrice' })),
    sellDetailsColumnHelper.accessor('priceTimestamp', dateTimeCol({ id: 'priceTimestamp' })),
    sellDetailsColumnHelper.accessor('priceSource', textCol({ id: 'priceSource' })),
    sellDetailsColumnHelper.accessor('accruedInterest', moneyCol({ id: 'accruedInterest' })),
];

const buyDetailsColumnHelper = createColumnHelper<BondLadderTradePlanBuyDetails>();
const buyDetailsColumns = [
    buyDetailsColumnHelper.accessor('sId', textCol({ id: 'sId' })),
    buyDetailsColumnHelper.accessor('quantity', numberCol({ id: 'quantity' })),
    buyDetailsColumnHelper.accessor('dirtyPrice', moneyCol({ id: 'dirtyPrice' })),
    buyDetailsColumnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
    buyDetailsColumnHelper.accessor('finalBudget', moneyCol({ id: 'finalBudget' })),
    buyDetailsColumnHelper.accessor('askPrice', moneyCol({ id: 'askPrice' })),
    buyDetailsColumnHelper.accessor('bidPrice', moneyCol({ id: 'bidPrice' })),
    buyDetailsColumnHelper.accessor('priceTimestamp', dateTimeCol({ id: 'priceTimestamp' })),
    buyDetailsColumnHelper.accessor('priceError', textCol({ id: 'priceError' })),
    buyDetailsColumnHelper.accessor('priceSource', textCol({ id: 'priceSource' })),
    buyDetailsColumnHelper.accessor('accruedInterest', moneyCol({ id: 'accruedInterest' })),
    buyDetailsColumnHelper.accessor('contractMultiplier', numberCol({ id: 'contractMultiplier' })),
    buyDetailsColumnHelper.accessor('priceMultiplier', numberCol({ id: 'priceMultiplier' })),
    buyDetailsColumnHelper.accessor('cusip', textCol({ id: 'cusip' })),
    buyDetailsColumnHelper.accessor('status', textCol({ id: 'status' })),
    buyDetailsColumnHelper.accessor('faceValue', moneyCol({ id: 'faceValue' })),
    buyDetailsColumnHelper.accessor('calculatedQuantity', numberCol({ id: 'calculatedQuantity' })),
];
